import React from 'react';

import { DataSource, Organisation } from '@futrli-sdk/organisations';
import { Integration } from '@futrli-sdk/app-store';

import { SyncControlDatasourceState, SyncControlState, SyncControlVariant } from './types';
import { SyncControl } from './components/sync-control/sync-control';
import { SyncControlExcel } from './components/sync-control/sync-control-excel';
import { SyncControlLoading } from './components/sync-control-loading/sync-control-loading';
import { SyncSidebarContainerContent } from './components/sync-sidebar-container-content/sync-sidebar-container-content';

interface Props {
  syncControlState: SyncControlState;
  workspaceId: string;
  organisation: Organisation | null;
  datasource?: DataSource;
  isExcelOrganisation: boolean;
  datasourceState: SyncControlDatasourceState;
  errorMessage?: React.ReactNode;
  syncingMessage?: React.ReactNode;
  showSyncNowLabel?: boolean;
  syncTimestamp: string;
  reconnect: (successUrl?: string) => Promise<void>;
  handleOauth2SyncNow: () => Promise<void>;
  integration?: Integration;
  unreconciledTransactionsCount?: number | null;
  variant: SyncControlVariant;
  needsReconnect?: boolean;
}

export const SyncControlContainerContent = ({
  syncControlState,
  workspaceId,
  organisation,
  integration,
  datasource,
  isExcelOrganisation,
  datasourceState,
  errorMessage,
  syncingMessage,
  showSyncNowLabel,
  syncTimestamp,
  reconnect,
  handleOauth2SyncNow,
  unreconciledTransactionsCount,
  variant,
  needsReconnect
}: Props) => {
  if (!workspaceId || !organisation || !datasource) {
    return null;
  }

  if (variant === SyncControlVariant.SIDEBAR) {
    return (
      <SyncSidebarContainerContent
        organisation={organisation}
        integration={integration}
        isExcelOrganisation={isExcelOrganisation}
        datasourceState={datasourceState}
        syncHistory={datasource.sync_history}
        syncTimestamp={syncTimestamp}
        unreconciledTransactionsCount={unreconciledTransactionsCount}
        handleOauth2SyncNow={handleOauth2SyncNow}
        syncControlState={syncControlState}
      />
    );
  }

  if ([SyncControlState.LOADING, SyncControlState.LOAD_ERROR].includes(syncControlState)) {
    return <SyncControlLoading error={syncControlState === SyncControlState.LOAD_ERROR} />;
  }

  if (isExcelOrganisation) {
    return (
      <SyncControlExcel
        datasourceState={datasourceState}
        errorMessage={errorMessage}
        organisation={organisation}
        syncControlState={syncControlState}
        syncTimestamp={syncTimestamp}
        syncingMessage={syncingMessage}
        showSyncNowLabel={showSyncNowLabel}
        workspaceId={workspaceId}
      />
    );
  }

  return (
    <SyncControl
      datasourceState={datasourceState}
      errorMessage={errorMessage}
      onReconnect={reconnect}
      onSyncNow={handleOauth2SyncNow}
      syncControlState={syncControlState}
      syncTimestamp={syncTimestamp}
      syncingMessage={syncingMessage}
      showSyncNowLabel={showSyncNowLabel}
      hideTriggerSync={datasource.type === 'sage-active'}
      needsReconnect={needsReconnect}
    />
  );
};
